import { useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SendIcon from '@mui/icons-material/Send';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  useStoryStudioSessionById,
  useUpsertStoryStudioById,
} from '../api/queries/storyStudio';
import { useProject } from '../api/queries/projects';
import { FilterableSelect } from '../components/common/FilterableSelect';
import { ProjectBreadcrumb } from '../components/common/ProjectBreadcrumb';
import { useAIAction } from '../hooks/useAIAction';
import { useGenerationStatus } from '../hooks/useGenerationStatus';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  mode: string;
  timestamp: Date;
  accepted?: boolean;
  editing?: boolean;
}

const MODES = [
  { value: 'write', label: 'Write' },
  { value: 'expand', label: 'Expand' },
  { value: 'rewrite', label: 'Rewrite' },
  { value: 'continue', label: 'Continue' },
  { value: 'summarize', label: 'Summarize' },
];

export function StoryStudioSessionRoute() {
  const { sessionId } = useParams({ strict: false }) as {
    sessionId: string;
  };
  const navigate = useNavigate();
  const session = useStoryStudioSessionById(sessionId);
  const upsert = useUpsertStoryStudioById(sessionId);
  const textAction = useAIAction('text-generation');

  const [synopsis, setSynopsis] = useState('');
  const [worldNotes, setWorldNotes] = useState('');
  const [mood, setMood] = useState('');
  const [pace, setPace] = useState('');
  const [voice, setVoice] = useState('');
  const [contextExpanded, setContextExpanded] = useState(true);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [input, setInput] = useState('');
  const [chatMode, setChatMode] = useState('write');
  const [isGenerating, setIsGenerating] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [toast, setToast] = useState('');
  const chatEndRef = useRef<HTMLDivElement>(null);

  const genStatus = useGenerationStatus(jobId);

  const projectId = session.data?.projectId ?? undefined;
  const { data: project } = useProject(projectId);

  useEffect(() => {
    if (!session.data) return;
    setSynopsis(session.data.synopsis ?? '');
    setWorldNotes(session.data.worldBuildingNotes ?? '');
    setMood(session.data.mood ?? '');
    setPace(session.data.pace ?? '');
    setVoice(session.data.narrativeVoice ?? '');
  }, [session.data?.id]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  useEffect(() => {
    if (!genStatus.event || genStatus.event.status !== 'complete') return;
    const payload = genStatus.event.payload as { result?: string } | undefined;
    if (payload?.result) {
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: payload.result!,
          mode: chatMode,
          timestamp: new Date(),
        },
      ]);
    }
    setIsGenerating(false);
    setJobId(null);
  }, [genStatus.event]);

  const handleSave = () => {
    upsert.mutate(
      {
        synopsis,
        worldBuildingNotes: worldNotes,
        mood,
        pace,
        narrativeVoice: voice,
      },
      { onSuccess: () => setToast('Session saved') },
    );
  };

  const buildSystemPrompt = () => {
    const parts: string[] = [];
    if (synopsis) parts.push(`Synopsis: ${synopsis}`);
    if (mood) parts.push(`Mood: ${mood}`);
    if (pace) parts.push(`Pace: ${pace}`);
    if (voice) parts.push(`Narrative voice: ${voice}`);
    if (draft) parts.push(`Current draft:\n${draft}`);
    return parts.join('\n\n');
  };

  const handleSend = async () => {
    if (!input.trim() || isGenerating) return;
    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: input.trim(),
      mode: chatMode,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsGenerating(true);

    try {
      const result = await textAction.mutateAsync({
        prompt: userMsg.content,
        system_prompt: buildSystemPrompt(),
        max_tokens: 1000,
        temperature: 0.7,
      });
      if (result.status === 'complete' && result.result) {
        const text =
          typeof result.result === 'string'
            ? result.result
            : ((result.result as { text?: string })?.text ??
              JSON.stringify(result.result));
        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: 'assistant',
            content: text,
            mode: chatMode,
            timestamp: new Date(),
          },
        ]);
        setIsGenerating(false);
      } else if (result.jobId) {
        setJobId(result.jobId);
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: 'Generation failed. Please try again.',
          mode: 'error',
          timestamp: new Date(),
        },
      ]);
      setIsGenerating(false);
    }
  };

  const acceptMessage = (msgId: string) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === msgId ? { ...m, accepted: true } : m)),
    );
    const msg = messages.find((m) => m.id === msgId);
    if (msg)
      setDraft((prev) => (prev ? prev + '\n\n' + msg.content : msg.content));
  };

  const rejectMessage = (msgId: string) => {
    setMessages((prev) => prev.filter((m) => m.id !== msgId));
  };

  const wordCount = draft.trim() ? draft.trim().split(/\s+/).length : 0;

  if (session.isLoading) return <CircularProgress />;
  if (session.isError) return <Typography>Could not load session.</Typography>;

  return (
    <Box
      sx={{
        height: 'calc(100vh - 100px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {project && projectId && (
        <ProjectBreadcrumb
          projectId={projectId}
          projectName={project.name}
          currentPage="Story Studio"
        />
      )}
      {/* Toolbar */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
        <Typography variant="h5" sx={{ flex: 1 }}>
          Story Studio
        </Typography>
        {session.data?.projectId && (
          <Chip
            size="small"
            label="Book-linked"
            color="primary"
            variant="outlined"
          />
        )}
        {!session.data?.projectId && (
          <Chip size="small" label="Standalone" variant="outlined" />
        )}
        <Button size="small" onClick={handleSave} disabled={upsert.isPending}>
          {upsert.isPending ? 'Saving...' : 'Save'}
        </Button>
      </Stack>

      {/* Two-panel layout */}
      <Stack direction="row" spacing={2} sx={{ flex: 1, minHeight: 0 }}>
        {/* Left: Chat */}
        <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
          <Accordion
            expanded={contextExpanded}
            onChange={(_, v) => setContextExpanded(v)}
            sx={{ mb: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">Session Context</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1.5}>
                <TextField
                  size="small"
                  label="Synopsis"
                  multiline
                  rows={2}
                  value={synopsis}
                  onChange={(e) => setSynopsis(e.target.value)}
                />
                <TextField
                  size="small"
                  label="World Notes"
                  multiline
                  rows={2}
                  value={worldNotes}
                  onChange={(e) => setWorldNotes(e.target.value)}
                />
                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    label="Mood"
                    value={mood}
                    onChange={(e) => setMood(e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    size="small"
                    label="Pace"
                    value={pace}
                    onChange={(e) => setPace(e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    size="small"
                    label="Voice"
                    value={voice}
                    onChange={(e) => setVoice(e.target.value)}
                    sx={{ flex: 1 }}
                  />
                </Stack>
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Messages */}
          <Box sx={{ flex: 1, overflowY: 'auto', mb: 1 }}>
            {messages.length === 0 && (
              <Typography
                color="text.secondary"
                variant="body2"
                sx={{ textAlign: 'center', mt: 4 }}
              >
                Start a conversation with the AI. Set your context above, then
                type an instruction below.
              </Typography>
            )}
            {messages.map((msg) => (
              <Box
                key={msg.id}
                sx={{
                  display: 'flex',
                  justifyContent:
                    msg.role === 'user' ? 'flex-end' : 'flex-start',
                  mb: 1,
                }}
              >
                <Paper
                  sx={{
                    p: 1.5,
                    maxWidth: '80%',
                    bgcolor: msg.role === 'user' ? 'primary.main' : 'grey.100',
                    color:
                      msg.role === 'user'
                        ? 'primary.contrastText'
                        : 'text.primary',
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {msg.content}
                  </Typography>
                  {msg.role === 'assistant' &&
                    !msg.accepted &&
                    msg.mode !== 'error' && (
                      <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => acceptMessage(msg.id)}
                        >
                          Accept
                        </Button>
                        <Button
                          size="small"
                          onClick={() => rejectMessage(msg.id)}
                        >
                          Reject
                        </Button>
                      </Stack>
                    )}
                  {msg.accepted && (
                    <Chip
                      size="small"
                      label="Added to draft"
                      color="success"
                      sx={{ mt: 0.5 }}
                    />
                  )}
                </Paper>
              </Box>
            ))}
            {isGenerating && (
              <Box
                sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}
              >
                <Paper sx={{ p: 1.5, bgcolor: 'grey.100' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      AI is thinking...
                    </Typography>
                  </Stack>
                </Paper>
              </Box>
            )}
            <div ref={chatEndRef} />
          </Box>

          {/* Input */}
          <Stack direction="row" spacing={1}>
            <FilterableSelect
              value={chatMode}
              onChange={(newValue) => setChatMode(newValue || 'write')}
              allowEmpty={false}
              options={MODES}
              sx={{ width: 120 }}
            />
            <TextField
              size="small"
              fullWidth
              placeholder="Type an instruction..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              multiline
              maxRows={3}
            />
            <IconButton
              onClick={handleSend}
              disabled={!input.trim() || isGenerating}
              color="primary"
            >
              <SendIcon />
            </IconButton>
          </Stack>
        </Paper>

        {/* Right: Draft */}
        <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ flex: 1 }}>
              Working Draft
            </Typography>
            <Button
              size="small"
              variant="outlined"
              disabled={!draft.trim()}
              onClick={() => {
                if (!draft.trim()) return;
                navigator.clipboard.writeText(draft);
                const bookId = session.data?.projectId;
                if (bookId) {
                  setToast(
                    'Draft copied to clipboard — paste into the Book Editor',
                  );
                  navigate({
                    to: '/books/$bookId',
                    params: { bookId },
                  });
                } else {
                  setToast(
                    'Draft copied to clipboard. Link this session to a book, then paste in the Book Editor.',
                  );
                }
              }}
            >
              Send to Book Editor
            </Button>
          </Stack>
          <TextField
            multiline
            fullWidth
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Accepted AI prose appears here. You can also type directly."
            sx={{
              flex: 1,
              '& .MuiInputBase-root': {
                height: '100%',
                alignItems: 'flex-start',
              },
            }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.5, textAlign: 'right' }}
          >
            {wordCount.toLocaleString()} words
          </Typography>
        </Paper>
      </Stack>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast('')}
        message={toast}
      />
    </Box>
  );
}
